import { useCallback, useEffect, useRef, useState } from "react";
import { API, updateHorse } from "@/lib/api";

const SECTIONS = ["horse_info", "seller_info", "sale_info"];
const keyFor = (id) => `bahs-adtpl-${id}`;
const isEmpty = (d) => !Object.keys(d).length;

export function mergeDirty(a, b) {
  const out = { ...a };
  Object.entries(b).forEach(([k, v]) => {
    if (v === undefined) return;
    out[k] = SECTIONS.includes(k) ? { ...(a[k] || {}), ...Object.fromEntries(Object.entries(v || {}).filter(([, x]) => x !== undefined)) } : v;
  });
  return out;
}

export function applyDirty(horse, d) {
  const h = { ...horse, write_up: { ...(horse.write_up || {}) } };
  SECTIONS.forEach((s) => { if (d[s]) h[s] = { ...(h[s] || {}), ...d[s] }; });
  if (d.write_up_original !== undefined) h.write_up.original = d.write_up_original;
  if (d.write_up_final !== undefined) h.write_up.final_edited = d.write_up_final;
  if (d.selected_version !== undefined) h.write_up.selected_version = d.selected_version;
  return h;
}

export const readPending = (id) => {
  try { return JSON.parse(localStorage.getItem(keyFor(id)) || "null"); } catch { return null; }
};

// Debounced server-side draft saving with a local (offline) safety copy.
export function useAutosave(id, setHorse, onSaved, delay = 1500) {
  const dirty = useRef({});
  const timer = useRef(null);
  const inflight = useRef(null);
  const [status, setStatus] = useState("saved");

  const persistLocal = useCallback(() => {
    if (isEmpty(dirty.current)) localStorage.removeItem(keyFor(id));
    else localStorage.setItem(keyFor(id), JSON.stringify({ at: new Date().toISOString(), d: dirty.current }));
  }, [id]);

  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    if (inflight.current) await inflight.current.catch(() => {});
    if (isEmpty(dirty.current)) return true;
    if (!navigator.onLine) { setStatus("offline"); return false; }
    const snap = dirty.current;
    dirty.current = {};
    setStatus("saving");
    const p = updateHorse(id, snap);
    inflight.current = p;
    try {
      const updated = await p;
      if (!updated || updated.id !== id) throw new Error("unexpected save response");
      setHorse(() => applyDirty(updated, dirty.current));
      persistLocal();
      setStatus(isEmpty(dirty.current) ? "saved" : "pending");
      onSaved?.();
      return true;
    } catch (e) {
      dirty.current = mergeDirty(snap, dirty.current);
      persistLocal();
      setStatus(e.response?.status === 403 ? "locked" : "error");
      return false;
    } finally { inflight.current = null; }
  }, [id, setHorse, onSaved, persistLocal]);

  const queue = useCallback((patch) => {
    dirty.current = mergeDirty(dirty.current, patch);
    persistLocal();
    setStatus("pending");
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, delay);
  }, [delay, flush, persistLocal]);

  useEffect(() => {
    const online = () => flush();
    const offline = () => { if (!isEmpty(dirty.current)) setStatus("offline"); };
    const leave = () => {
      if (isEmpty(dirty.current)) return;
      fetch(`${API}/horses/${id}`, { method: "PATCH", credentials: "include", keepalive: true,
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(dirty.current) }).catch(() => {});
    };
    const hidden = () => { if (document.visibilityState === "hidden") leave(); };
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    window.addEventListener("pagehide", leave);
    document.addEventListener("visibilitychange", hidden);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
      window.removeEventListener("pagehide", leave);
      document.removeEventListener("visibilitychange", hidden);
      clearTimeout(timer.current);
      if (!isEmpty(dirty.current)) flush();
    };
  }, [id, flush]);

  return { status, queue, flush, retry: flush, hasPending: () => !isEmpty(dirty.current) };
}
